import { Component, OnDestroy, OnInit } from '@angular/core';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { ApiAddressOut, ApiClientAccountOut, ClientAccountAdditionNewIn, ClientAccountAdditionOut, DisabledAdditionReasons } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { defaultRiskLevels } from 'src/app/shared/constants';
import { RedirectService } from 'src/app/services/redirect.service';
import { TranslatePipe } from '@ngx-translate/core';
import { interval, Subscription } from 'rxjs';
import { SignalsService } from 'src/app/services/signals.service';
import { IosWorkaroundService } from 'src/app/services/ios-workaround.service';
import { InstrumentCategories } from 'src/app/shared/charts/charts';
import { switchMap } from 'rxjs/operators';
import dayjs from 'dayjs';

@Component({
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  standalone: false
})
export class UserComponent implements OnInit, OnDestroy {
  public riskLevels = defaultRiskLevels;
  public preloaders = {
    userData: true,
    additions: true,
  }
  public userData: ApiClientAccountOut | null = null;
  public additionsData: ClientAccountAdditionOut[] | null = null;
  public additionsDisabled: {
    executionOnlySwitch: false | DisabledAdditionReasons;
    portfoliosMigrate: false | DisabledAdditionReasons;
    feesConfigNotAllowed: boolean;
    feesConfigProcessing: false | DisabledAdditionReasons;
  } = {
    executionOnlySwitch: false, // INVEST_QUEST_INSTANT_CATEGORY
    portfoliosMigrate: false, // PM_TO_EO_CONVERSION
    feesConfigNotAllowed: false, // OPF_TRANSFER - not allowed
    feesConfigProcessing: false, // OPF_TRANSFER - in process
  };


  private additionsRefetchSub?: Subscription;
  private dataRefetchSub?: Subscription;
  private modalCloseSub?: Subscription;

  public get sliderMax(): number {
    return Math.max(...this.riskLevels);
  }

  public constructor(
    private restApiService: RestApiService,
    private loggerService: LoggerService,
    public toastService: ToastNotificationService,
    public modalService: ModalService,
    public iosWorkaroundService: IosWorkaroundService,
    private redirectService: RedirectService,
    private translatePipe: TranslatePipe,
    private signalsService: SignalsService,
  ) { }

  public ngOnInit(): void {
    this.loadUserData();
  }

  private loadUserData(): void {
    this.unsubEtf();
    this.restApiService.getUserDetail().subscribe({
      error: this.handleUserDetailError.bind(this),
      next: this.handleUserDetailyData.bind(this),
    });
    this.restApiService.getClientAdditions().subscribe({
      error: this.handleAdditionsError.bind(this),
      next: this.handleAdditionsData.bind(this),
    });
  }

  private handleUserDetailError(error: unknown): void {
    this.preloaders.userData = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private handleAdditionsError(error: unknown): void {
    this.preloaders.additions = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private handleAdditionsData(data: ClientAccountAdditionOut[]): void {
    this.preloaders.additions = false;
    this.additionsData = data;
    this.updateAdditionsDisable(data);
  }

  private updateAdditionsDisable(data: ClientAccountAdditionOut[]): void {
    this.additionsDisabled.executionOnlySwitch = data.find(addition => addition.type === ClientAccountAdditionNewIn.INVEST_QUEST_INSTANT_CATEGORY)?.reasonWhyNotList[0] ?? false;
    this.additionsDisabled.portfoliosMigrate = data.find(addition => addition.type === ClientAccountAdditionNewIn.PM_TO_EO_CONVERSION)?.reasonWhyNotList[0] ?? false;

    const feesConfig = data.find(addition => addition.type === ClientAccountAdditionNewIn.OPF_TRANSFER)?.reasonWhyNotList[0];
    this.additionsDisabled.feesConfigProcessing = feesConfig ?? false;
    this.additionsDisabled.feesConfigNotAllowed =
      feesConfig === DisabledAdditionReasons.NOT_ALLOWED_BY_COLLABORATOR ||
      feesConfig === DisabledAdditionReasons.NO_FRAMEWORK_CONTRACT_TO_TRANSFER;

    // periodic check if user additions changed
    this.additionsRefetchSub?.unsubscribe();
    this.additionsRefetchSub = interval(60000).pipe(
      switchMap(() => {
        this.preloaders.additions = true;
        return this.restApiService.getClientAdditions();
      })
    ).subscribe({
      next: this.handleAdditionsData.bind(this),
      error: this.handleAdditionsError.bind(this),
    });
  }

  private handleUserDetailyData(data: ApiClientAccountOut): void {
    this.preloaders.userData = false;
    this.userData = data;
  }

  public get userInstrumentCategories(): InstrumentCategories[] {
    if (!this.userData?.investmentQuestionnaire?.instrumentCategories) {
      return [];
    }
    return this.getTranslatedInstrumentCategoriesSorted(
      this.userData.investmentQuestionnaire.instrumentCategories
    );
  }

  private getTranslatedInstrumentCategoriesSorted(categories: InstrumentCategories[]): InstrumentCategories[] {
  return categories
    .map(cat => this.translatePipe.transform('investmentChartCategories.' + cat))
    .sort();
  }

  public get userDomicileAddress(): ApiAddressOut {
    return this.userData!.addresses.find(address => address.type === 'DOMICILE') ?? {} as ApiAddressOut;
  }

  public get userPostalAddress(): ApiAddressOut | null {
    return this.userData?.addresses.find(address => address.type === 'POSTAL') ?? null;
  }

  public get postAddressSameAsDomicile(): boolean {
    if (
      this.userData &&
      this.userPostalAddress !== null
    ) {
      return false;
    }

    return true;
  }

  public goToInvestmentQuestionaireForm(): void {
    this.redirectService.goToInvestmentQuestionaireExternalForm();
  }

  public getPortfolioTypesIdxsByMax(maxRiskScore: number): number[] {
    return Array.from({length: maxRiskScore}, (_, i) => i + 1);
  }

  public portfolioTypesSeparator(idx: number): string {
    if (!this.userData)
      return '';

    // determines whether to add comma, conjuction for -and/a- or empty space to the string list
    if (idx < this.userData.maxRiskScore - 1) {
      return ', ';
    } else if (idx !== this.userData.maxRiskScore) {
      return ' ' + this.translatePipe.transform('global.and') + ' ';
    } else {
      return '';
    }
  }

  public openConfirmEtfUpdate(): void {
    this.modalService.openConfirmEtfUpdate();

    this.dataRefetchSub = this.signalsService.confirmEtf$.subscribe(reload => {
      if(reload) {
        this.preloaders.userData = true;
        this.loadUserData();
      }
    });

    this.modalCloseSub = this.signalsService.modalClosed$.subscribe(() => {
      this.unsubEtf();
    });
  }

  private unsubEtf(): void {
    if(this.dataRefetchSub) {
      this.dataRefetchSub.unsubscribe();
    }
    if(this.modalCloseSub) {
      this.modalCloseSub.unsubscribe();
    }
  }

  public displayOpfTransferBtn(): boolean {
    // todo - whole button can be removed after 2026-01-01
    return !this.additionsDisabled.feesConfigNotAllowed && dayjs().isBefore(dayjs('2026-01-01 00:00', 'YYYY-MM-DD'));
  }

  public ngOnDestroy(): void {
    this.unsubEtf();
    this.additionsRefetchSub?.unsubscribe();
  }
}
